// ytSuggest.ts — turn YouTube's own "most replayed" heatmap + named chapters
// (fetched server-side via /api/yt-markers) into up to 3 one-tap practice-loop
// suggestions. computeSuggestions() is ported verbatim from the Riffloop
// extension (Moshe's tuning). The Studio shows these as chips under the capture
// waveform + amber bands; tapping one sets the A→B selection.

export interface Suggestion {
  kind: 'chapter' | 'heat';
  start: number; // seconds
  end: number; // seconds
  peak: number; // seconds — the hottest point
  label: string;
}

interface HeatBucket { t: number; d: number; v: number }
interface Chapter { t: number; end: number; title: string }

const SUG_MAX = 3;
// Chapter titles worth suggesting as practice loops.
const SUG_CHAPTER_RX = /\bsolo\b|\binstrumental\b|\briff\b|guitar\s*break|\bbridge\b/i;

export function computeSuggestions(heat: HeatBucket[], chapters: Chapter[], duration: number): Suggestion[] {
  const items: (Suggestion & { score: number })[] = [];
  const dur = duration || 0;

  // Chapters first — a section the creator literally named beats crowd heat.
  if (Array.isArray(chapters) && chapters.length) {
    const stale = dur > 0 && chapters[chapters.length - 1].t > dur;
    if (!stale) {
      for (const ch of chapters) {
        if (!SUG_CHAPTER_RX.test(ch.title || '')) continue;
        const start = Math.max(0, +ch.t || 0);
        const end = Math.min(dur || +ch.end || 0, +ch.end || 0);
        if (end - start < 2) continue;
        items.push({
          kind: 'chapter', start, end, peak: start,
          label: String(ch.title).trim().slice(0, 28), score: 2 + (+ch.t || 0) * 1e-6,
        });
        if (items.length >= SUG_MAX) break;
      }
    }
  }

  // Heatmap: contiguous runs ≥ 65% of the max replay intensity. The first
  // seconds are excluded BEFORE computing the max (everyone presses play at
  // 0:00, so the intro buckets always read near 1.0 and would swamp it).
  const INTRO_SKIP = 15;
  if (Array.isArray(heat) && heat.length > 3) {
    const last = heat[heat.length - 1];
    const coverage = (+last.t || 0) + (+last.d || 0);
    const stale = dur > 0 && Math.abs(coverage - dur) / dur > 0.15;
    const usable = heat.filter((h) => (+h.t || 0) >= INTRO_SKIP);
    const max = usable.reduce((m, h) => Math.max(m, +h.v || 0), 0);
    if (!stale && max > 0) {
      const thr = 0.65 * max;
      const runs: { start: number; end: number; peak: number; peakV: number }[] = [];
      let run: { start: number; end: number; peak: number; peakV: number } | null = null;
      for (const h of usable) {
        const t = +h.t || 0;
        const d = +h.d || 0;
        const v = +h.v || 0;
        if (v >= thr) {
          if (!run) run = { start: t, end: t + d, peak: t + d / 2, peakV: v };
          else {
            run.end = t + d;
            if (v > run.peakV) {
              run.peakV = v;
              run.peak = t + d / 2;
            }
          }
        } else if (run) {
          runs.push(run);
          run = null;
        }
      }
      if (run) runs.push(run);
      runs.sort((a, b) => b.peakV - a.peakV);
      for (const r of runs) {
        if (r.end - r.start < 3) {
          r.start = Math.max(0, r.peak - 5);
          r.end = Math.min(dur || r.peak + 5, r.peak + 5);
        }
        items.push({ kind: 'heat', start: r.start, end: r.end, peak: r.peak, label: 'Most replayed', score: r.peakV });
        if (items.filter((i) => i.kind === 'heat').length >= 2) break;
      }
    }
  }

  // Drop a heat region that mostly overlaps a chapter (the chapter has a name).
  const kept: (Suggestion & { score: number })[] = [];
  for (const it of items) {
    const dup = kept.find((k) => Math.min(k.end, it.end) - Math.max(k.start, it.start) > 0.5 * (it.end - it.start));
    if (!dup) kept.push(it);
  }
  kept.sort((a, b) => a.start - b.start);
  return kept.slice(0, SUG_MAX).map(({ score: _score, ...s }) => s);
}

/** Fetch YouTube's markers via our proxy and turn them into loop suggestions. */
export async function fetchYtSuggestions(videoId: string, durationSec: number): Promise<Suggestion[]> {
  try {
    const res = await fetch('/api/yt-markers?v=' + encodeURIComponent(videoId), { credentials: 'include' });
    if (!res.ok) return [];
    const d = await res.json();
    if (!d || !d.ok) return [];
    return computeSuggestions(d.heat || [], d.chapters || [], durationSec);
  } catch {
    return [];
  }
}
